import { ReactNode } from 'react';
import { CarePhase } from '@shared/appModel';
import {
  DAYS_IN_MONTH,
  ONE_ADL_DURATION_MULTIPLIER,
} from '@shared/calculations/funding/projection/sharedConstants';
import { ReasonText } from './ReasonText';
import { BreakdownLine, ReasonKey, ReasonLabel } from './segments';

/**
 * Renders a per-reason day breakdown as a stacked list — one row per reason
 * with `Nd` to the right. Falls back to the primary reason label with no day
 * count when the breakdown is empty (rare: a non-paying row with no classified
 * ineligible days).
 *
 * `tooltips` provides per-key rich tooltip overrides (e.g. the <2 ADL
 * derivation tooltip). Reasons without an override fall back to the static
 * `REASON_TOOLTIPS` string inside `ReasonText`.
 */
export function ReasonBreakdown({
  lines,
  fallback,
  variant,
  tooltips,
}: {
  lines: BreakdownLine[];
  fallback: ReasonLabel;
  variant: 'summary' | 'detail';
  tooltips?: Partial<Record<ReasonKey, ReactNode>>;
}) {
  const reasonClassName = variant === 'detail' ? 'text-xs text-gray-600' : undefined;
  const dayClassName =
    variant === 'detail'
      ? 'ml-2 text-[10px] tabular-nums text-gray-400'
      : 'ml-2 text-[11px] tabular-nums text-gray-500';

  if (lines.length === 0) {
    return (
      <ReasonText
        reason={fallback}
        className={reasonClassName}
        tooltipContent={tooltips?.[fallback.key]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {lines.map((line, i) => (
        <ReasonText
          key={`${line.reason.key}-${i}`}
          reason={line.reason}
          className={reasonClassName}
          trailing={<span className={dayClassName}>· {line.days}d</span>}
          tooltipContent={tooltips?.[line.reason.key]}
        />
      ))}
    </div>
  );
}

/**
 * Tooltip body for the "Less than 2 ADLs active" badge — shows the rule plus
 * the math derivation for this client's early-care phase. The bucket size is
 * `roundedPhaseMonths × DAYS_IN_MONTH × ONE_ADL_DURATION_MULTIPLIER`, the
 * same formula `computeNon2AdlDaysThisRow` uses in the calc — kept here only
 * for display, never re-derived for math.
 */
function Lt2AdlTooltipContent({ phaseCareMonths }: { phaseCareMonths: number }) {
  const roundedPhaseMonths = Math.round(phaseCareMonths);
  const earlyCareDays = roundedPhaseMonths * DAYS_IN_MONTH;
  const bucketDays = earlyCareDays * ONE_ADL_DURATION_MULTIPLIER;
  const bucketMonths = bucketDays / DAYS_IN_MONTH;
  return (
    <>
      <div className="mb-1 font-semibold">Less than 2 ADLs active</div>
      <div className="mb-2 text-[10px] text-gray-300">
        The policy treats the first half of the early-care phase as the &lt;2&nbsp;ADL ineligibility
        bucket — until the client needs ≥2 ADLs of assistance, benefits don't engage.
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <span className="text-gray-300">Early-care duration</span>
        <span className="text-right font-semibold">{phaseCareMonths.toFixed(1)} mo</span>
        <span className="text-gray-300">× ½ (phase fraction)</span>
        <span className="text-right">× {ONE_ADL_DURATION_MULTIPLIER}</span>
        <span className="text-gray-300">× days per month</span>
        <span className="text-right">× {DAYS_IN_MONTH}</span>
        <span
          className="col-span-2 my-1 border-t border-gray-600"
          aria-hidden
        />
        <span className="font-semibold">= &lt;2 ADL bucket</span>
        <span className="text-right font-semibold">
          {bucketDays} d
          <span className="ml-1 font-normal text-gray-400">({bucketMonths.toFixed(1)} mo)</span>
        </span>
      </div>
    </>
  );
}

/**
 * Tooltip body for the "Before policy start" badge. Before the policy's
 * premium-start year the policy doesn't exist, so the whole month is
 * out-of-pocket no matter the care details — these months are grouped under a
 * single "before policy start" reason rather than split by the policy's
 * eligibility gates. The <2 ADL window (always half the early-care phase) and
 * the elimination period still exist on the care timeline; they just don't
 * change anything until the policy is in force and can actually pay.
 *
 * `careStartYear` is the onset on the SAME policy-year clock that drove the
 * pre-policy classification (premiumStart + policyYear − 1), so the date rows
 * are self-consistent with the badge.
 */
function PrePolicyTooltipContent({
  careStartYear,
  policyPremiumStartYear,
}: {
  careStartYear: number;
  policyPremiumStartYear: number;
}) {
  return (
    <>
      <div className="mb-1 font-semibold">Before policy start</div>
      <div className="mb-2 text-[10px] text-gray-300">
        This care is projected to begin before the policy&apos;s premium-start year, so the policy
        does not yet exist and can&apos;t pay — the full cost is out of pocket regardless of the
        care details. The &lt;2&nbsp;ADL window and elimination (waiting) period only affect what
        the policy pays once it&apos;s in force, so these months aren&apos;t split by those reasons.
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <span className="text-gray-300">Care predicted to start</span>
        <span className="text-right font-semibold">{careStartYear}</span>
        <span className="text-gray-300">Policy begins</span>
        <span className="text-right font-semibold">{policyPremiumStartYear}</span>
      </div>
    </>
  );
}

/**
 * Resolves per-reason rich-tooltip overrides for a single row's data. Pulls
 * the row's carePhase off the entry and the matching phaseCareMonths off
 * terms — both are needed to derive the per-phase bucket size shown in the
 * <2 ADL tooltip. Returns an empty map when terms aren't available or the
 * reason isn't one we have a rich tooltip for, so `ReasonText` falls back
 * to the static REASON_TOOLTIPS string.
 */
export function buildReasonTooltips(
  month: MonthlyEntry,
  terms: ClaimTerms | undefined,
): Partial<Record<ReasonKey, ReactNode>> {
  if (!terms) return {};
  const tooltips: Partial<Record<ReasonKey, ReactNode>> = {};
  const carePhase = month.context.carePhase;
  const phaseMonths = terms.phaseCareMonthsByPhase?.[carePhase];
  // The 2-ADL gate only fires in earlyCare (see sharedEligibility), so the
  // derivation tooltip only makes sense there. Missing phase data → fall
  // back to the static string.
  if (carePhase === CarePhase.earlyCare && phaseMonths != null) {
    tooltips['lt-2-adl'] = <Lt2AdlTooltipContent phaseCareMonths={phaseMonths} />;
  }
  // Pre-policy rows: show the care-start vs. premium-start years that make the
  // month pre-policy. Derive the care-start year from the SAME policy-year clock
  // that drives the classification (premiumStart + policyYear − 1), NOT from
  // `facts.projectedCareYear` — the two can disagree when a partner's onset is
  // resolved from an age fallback for the clock but a "today" fallback for the
  // cost projection, which would print a self-contradictory year here.
  const careStartYear = terms.policyPremiumStartYear + month.context.policyYear - 1;
  tooltips['pre-policy'] = (
    <PrePolicyTooltipContent
      careStartYear={careStartYear}
      policyPremiumStartYear={terms.policyPremiumStartYear}
    />
  );
  return tooltips;
}
